/**
 * GameListAdapter - Fetches game data from the API
 *
 * The database is the single source of truth for all game data including images.
 * Images should be stored as URLs (e.g., Azure Blob Storage URLs) in the database.
 *
 * Caching Strategy:
 * - In-memory cache only (no localStorage) - fresh data on each page load
 * - This ensures game changes (purchases, admin disable/enable) are reflected immediately
 * - Force refresh option available for explicit cache bypass
 *
 * Data Model:
 * - Products (cachedProducts): Purchasable items from GamePack table (store, cart, checkout)
 * - Playable Games (cachedPlayableGames): Individual games from Games table (lobby, ownership)
 *
 * Usage:
 * - Import this instead of GameList.js
 * - Call initializeGames() on app startup
 * - Use getGameList() for store/cart/checkout (returns products)
 * - Use getPlayableGames() for lobby/ownership display (returns games in product shape)
 * - Use findGameById() and findGameBySlug() for lookups
 */

import gamesService from "services/games.service.js";

// In-memory cache for fast access within a session
let cachedProducts = [];
let cachedPlayableGames = [];
let isLoading = false;
let loadError = null;
let isInitialized = false;

// Clean up old localStorage cache (one-time migration)
try {
    localStorage.removeItem('gameListCache');
} catch (e) {
    // Ignore errors
}

/**
 * Clear the in-memory cache
 */
export const clearGamesCache = () => {
    cachedProducts = [];
    cachedPlayableGames = [];
    isInitialized = false;
    console.log('GameListAdapter: Cache cleared');
};

/**
 * Wraps a game from the games[] API response into the product shape
 * so existing components (Game.js, GameVote.js, etc.) work without changes.
 * Maps flat game fields into nested meta/gameInfo sub-objects.
 */
const wrapGameAsProduct = (game) => {
    return {
        // Identity
        gameId: game.gameId,
        name: game.name,
        gameServerName: game.gameServerName,
        status: game.status,
        description: game.description,
        // Gameplay
        logo: game.logo,
        gameUrl: game.gameUrl,
        iosGameId: game.iosGameId,
        androidGameId: game.androidGameId,
        supportedPlatforms: game.supportedPlatforms || [],
        gameShow: game.gameShow,
        partyGame: game.partyGame,
        tags: game.tags || [],
        // Nested objects matching product shape
        gameInfo: {
            minPlayers: game.minPlayers,
            maxPlayers: game.maxPlayers,
            avgRoundLengthText: game.avgRoundLengthText,
            minAge: game.minAge,
            lobbyBanner: game.lobbyBanner,
            lobbyCornerBanner: game.lobbyCornerBanner,
        },
        meta: {
            gameCard: game.gameCard,
            tinyCard: game.tinyCard,
            gameMenuTextColour: game.gameMenuTextColour,
            gameMenuBackgroundColour: game.gameMenuBackgroundColour,
            gameMenuBackground: game.gameMenuBackground,
            iconTheme: game.iconTheme,
            ageRatingIcon: game.ageRatingIcon,
            previewImages: game.previewImages,
        },
    };
};

/**
 * Initialize the game list from API
 * Call this on app startup (e.g., in App.js or index.js)
 * @param {boolean} forceRefresh - Force refresh from API even if cached in memory
 * @returns {Promise<Array>} The product list (for backwards compatibility)
 */
export const initializeGames = async (forceRefresh = false) => {
    // Return in-memory cache if already initialized and not forcing refresh
    if (isInitialized && cachedProducts.length > 0 && !forceRefresh) {
        return cachedProducts;
    }

    // Wait for existing load to complete
    if (isLoading) {
        return new Promise((resolve) => {
            const checkInterval = setInterval(() => {
                if (!isLoading) {
                    clearInterval(checkInterval);
                    resolve(cachedProducts);
                }
            }, 100);
        });
    }

    // Fetch from API
    isLoading = true;
    loadError = null;

    try {
        const apiResponse = await gamesService.getGames();

        // Handle new { games, products } response or legacy flat array
        let rawProducts;
        let rawGames;

        if (apiResponse && apiResponse.products && Array.isArray(apiResponse.products)) {
            // New structured response
            rawProducts = apiResponse.products;
            rawGames = apiResponse.games || [];
        } else if (Array.isArray(apiResponse)) {
            // Legacy flat array response (backwards compatibility)
            rawProducts = apiResponse;
            rawGames = [];
        } else {
            console.warn('GameListAdapter: Unexpected API response format');
            rawProducts = [];
            rawGames = [];
        }

        // Filter and deduplicate products
        const seenProducts = new Set();
        const validProducts = rawProducts.filter(game => {
            if (!game.gameId) {
                console.warn(`GameListAdapter: Skipping product with missing gameId: ${game.name || 'unnamed'}`);
                return false;
            }
            if (!game.productId) {
                console.warn(`GameListAdapter: Skipping product with missing productId: ${game.gameId}`);
                return false;
            }
            if (seenProducts.has(game.gameId)) {
                console.warn(`GameListAdapter: Duplicate product gameId found: ${game.gameId}`);
                return false;
            }
            seenProducts.add(game.gameId);
            return true;
        });

        // Filter and deduplicate playable games
        const seenGames = new Set();
        const validGames = rawGames.filter(game => {
            if (!game.gameId) {
                console.warn(`GameListAdapter: Skipping game with missing gameId: ${game.name || 'unnamed'}`);
                return false;
            }
            if (seenGames.has(game.gameId)) {
                console.warn(`GameListAdapter: Duplicate game gameId found: ${game.gameId}`);
                return false;
            }
            seenGames.add(game.gameId);
            return true;
        });

        cachedProducts = validProducts;
        cachedPlayableGames = validGames;
        isInitialized = true;

        console.log(`GameListAdapter: Loaded ${cachedProducts.length} products and ${cachedPlayableGames.length} playable games from API`);
        return cachedProducts;
    } catch (error) {
        console.error('GameListAdapter: Failed to load games from API', error);
        loadError = error;
        cachedProducts = [];
        cachedPlayableGames = [];
        return cachedProducts;
    } finally {
        isLoading = false;
    }
};

/**
 * Force refresh games from API, bypassing in-memory cache
 * @returns {Promise<Array>} The refreshed product list
 */
export const refreshGames = async () => {
    isInitialized = false;
    return initializeGames(true);
};

/**
 * Get the current product list (for store, cart, checkout, hero sections)
 * Returns cached products from API
 * @returns {Array} Array of product objects
 */
export const getGameList = () => {
    return cachedProducts;
};

/**
 * Get the playable games list (for lobby, ownership display, game selection)
 * Returns games wrapped into the product shape so existing components work unchanged.
 * @returns {Array} Array of game objects in product shape
 */
export const getPlayableGames = () => {
    return cachedPlayableGames.map(wrapGameAsProduct);
};

/**
 * Find a playable game by its gameId
 * @param {string} gameId - The game identifier
 * @returns {Object|undefined} The game object in product shape, or undefined
 */
export const findPlayableGameById = (gameId) => {
    const game = cachedPlayableGames.find(g => g.gameId === gameId);
    return game ? wrapGameAsProduct(game) : undefined;
};

/**
 * Find a product by its gameId (searches products only - for store/checkout context)
 * @param {string} gameId - The game identifier
 * @returns {Object|undefined} The product object or undefined
 */
export const findGameById = (gameId) => {
    return cachedProducts.find(g => g.gameId === gameId);
};

/**
 * Find a game by its store slug
 * @param {string} slug - The store slug (with or without /shop/ prefix)
 * @returns {Object|undefined} The game object or undefined
 */
export const findGameBySlug = (slug) => {
    const normalizedSlug = slug.startsWith('/shop/') ? slug : `/shop/${slug}`;
    return cachedProducts.find(g => g.storeSlug === normalizedSlug);
};

/**
 * Check if a game is enabled (status === "enabled")
 */
const isEnabled = (g) => g.status === "enabled";

/**
 * Get enabled games only
 * @returns {Array} Array of enabled product objects
 */
export const getEnabledGames = () => {
    return cachedProducts.filter(isEnabled);
};

/**
 * Get games marked as best sellers
 * @returns {Array} Array of best seller product objects
 */
export const getBestSellers = () => {
    return cachedProducts.filter(g => g.bestSeller && isEnabled(g));
};

/**
 * Get games marked as game shows
 * @returns {Array} Array of game show product objects
 */
export const getGameShows = () => {
    return cachedProducts.filter(g => g.gameShow && isEnabled(g));
};

/**
 * Get party games
 * @returns {Array} Array of party game product objects
 */
export const getPartyGames = () => {
    return cachedProducts.filter(g => g.partyGame && isEnabled(g));
};

/**
 * Get all addons
 * @returns {Array} Array of addon product objects
 */
export const getAddons = () => {
    return cachedProducts.filter(g => g.type === 'Addon');
};

/**
 * Get addons for a specific game
 * @param {string} gameId - The base game identifier
 * @returns {Array} Array of addon objects for that game
 */
export const getAddonsForGame = (gameId) => {
    return cachedProducts.filter(g => g.type === 'Addon' && g.addonIds?.includes(gameId));
};

/**
 * Get all game packs
 * @returns {Array} Array of game pack product objects
 */
export const getGamePacks = () => {
    return cachedProducts.filter(g => g.type === 'GamePack');
};

/**
 * Check if games have been loaded from API
 * @returns {boolean}
 */
export const isLoadedFromApi = () => {
    return isInitialized && loadError === null;
};

/**
 * Check if games are currently loading
 * @returns {boolean}
 */
export const isGamesLoading = () => isLoading;

/**
 * Get the load error if any
 * @returns {Error|null}
 */
export const getLoadError = () => loadError;

// Default export - the product list getter
export default cachedProducts;
